import { useRef, useState } from "react";
import { useConsulControlHealth } from "../../../hooks/useConsulControlStatus";
import { useSystemStatus } from "../../../hooks/useSystemStatus";
import { useGateway } from "../../../contexts/GatewayContext";
import {
  FOOTER_DATA_FONT_STYLE,
  FOOTER_HEADING_FONT_STYLE,
} from "./footer-toolbar-model";

export type FooterStatusLevel = "ok" | "degraded" | "error" | "unknown";

export interface FooterStatusItem {
  label: string;
  status: FooterStatusLevel;
  detail?: string;
}

const DOT_CLASS: Record<FooterStatusLevel, string> = {
  ok: "bg-emerald-400",
  degraded: "bg-yellow-400 animate-pulse",
  error: "bg-red-400",
  unknown: "bg-[var(--fintheon-muted)]/45",
};

const TEXT_CLASS: Record<FooterStatusLevel, string> = {
  ok: "text-emerald-400/70",
  degraded: "text-yellow-400/70",
  error: "text-red-400/70",
  unknown: "text-[var(--fintheon-muted)]",
};

export function useFooterStatusGroups() {
  const { services } = useSystemStatus();
  const { status: gatewayStatus } = useGateway();
  const consul = useConsulControlHealth();
  const gateway: FooterStatusItem = {
    label: "Gateway",
    status:
      gatewayStatus === "connected"
        ? "ok"
        : gatewayStatus === "connecting"
          ? "degraded"
          : "error",
    detail:
      gatewayStatus === "connected"
        ? "Backend reachable"
        : gatewayStatus === "connecting"
          ? "Connecting..."
          : "Disconnected",
  };
  const cloud = [
    gateway,
    findService(
      services,
      ["ai", "provider runtime"],
      "AI Runtime",
      "Awaiting AI runtime diagnostics",
    ),
    findService(
      services,
      ["database", "supabase"],
      "Database",
      "Awaiting database diagnostics",
    ),
    findService(services, ["x", "x-feed"], "X", "Awaiting X diagnostics"),
    findService(
      services,
      ["auth", "supabase auth"],
      "Auth",
      "Awaiting auth diagnostics",
    ),
    findService(
      services,
      ["forum", "livekit"],
      "Forum",
      "Awaiting Forum voice diagnostics",
    ),
  ];
  const tradingView = [
    findService(
      services,
      ["econ", "tradingview econ calendar"],
      "Econ",
      "Awaiting DeskPlan queue diagnostics",
    ),
    findService(
      services,
      ["quotes", "tradingview quotes"],
      "Quotes",
      "Awaiting quote diagnostics",
    ),
    { label: "QuickScout", status: consul.status, detail: consul.detail },
  ];
  return {
    cloud,
    cloudStatus: resolveGroupStatus(cloud),
    tradingView,
    tradingViewStatus: resolveGroupStatus(tradingView),
  };
}

export function FooterStatusGroup({
  label,
  status,
  items,
}: {
  label: string;
  status: FooterStatusLevel;
  items: FooterStatusItem[];
}) {
  const [open, setOpen] = useState(false);
  const hoverTimer = useRef<number | null>(null);
  const clearTimer = () => {
    if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  };
  const close = () => {
    clearTimer();
    setOpen(false);
  };
  return (
    <div
      className="relative"
      onMouseEnter={() => {
        clearTimer();
        hoverTimer.current = window.setTimeout(() => setOpen(true), 2000);
      }}
      onMouseLeave={close}
      onFocus={() => setOpen(true)}
      onBlur={close}
    >
      <button
        type="button"
        className="flex items-center gap-1.5 bg-transparent p-0"
        title={`${label}: ${status}`}
        aria-label={`${label} status: ${status}`}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${DOT_CLASS[status]}`} />
        <span
          className={`text-[9px] tracking-[0.14em] uppercase ${TEXT_CLASS[status]}`}
          style={FOOTER_HEADING_FONT_STYLE}
        >
          {label}
        </span>
      </button>
      {open ? (
        <div className="fintheon-popover-surface absolute bottom-full right-0 z-50 mb-1 w-56 px-3 py-2">
          <p
            className="mb-1.5 text-[9px] uppercase tracking-[0.14em] text-[var(--fintheon-muted)]"
            style={FOOTER_HEADING_FONT_STYLE}
          >
            {label}
          </p>
          {items.map((item) => (
            <div key={item.label} className="flex items-start gap-2 py-1">
              <span
                className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${DOT_CLASS[item.status]}`}
              />
              <div className="min-w-0">
                <p
                  className="text-[10px] text-[var(--fintheon-text)]/76"
                  style={FOOTER_DATA_FONT_STYLE}
                >
                  {item.label}
                </p>
                {item.detail ? (
                  <p className="truncate text-[9px] text-[var(--fintheon-muted)]/62">
                    {item.detail}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function resolveGroupStatus(items: FooterStatusItem[]): FooterStatusLevel {
  if (items.some((item) => item.status === "error")) return "error";
  if (items.some((item) => item.status === "degraded")) return "degraded";
  if (items.every((item) => item.status === "unknown")) return "unknown";
  return "ok";
}

function findService(
  services: Array<{
    key: string;
    name: string;
    status: FooterStatusLevel;
    detail?: string;
  }>,
  keys: string[],
  label: string,
  fallbackDetail: string,
): FooterStatusItem {
  const keySet = new Set(keys);
  const service = services.find(
    (item) => keySet.has(item.key) || keySet.has(item.name.toLowerCase()),
  );
  return service
    ? { label, status: service.status, detail: service.detail }
    : { label, status: "unknown", detail: fallbackDetail };
}
